import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import intl from 'react-intl-universal';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import { RootState, ThunkApi } from 'state/store';
import { ProjectTemplate } from 'types/store/normalized';
import { ProjectTemplate as LegacyProjectTemplate } from 'utils/types/templates';
import { SortingType, Status } from 'utils/customTypes';
import { SLICE_STATUS, TEMPLATE_SORTING } from 'utils/constants';
import projectTemplatesAPI from './projectTemplatesAPI';
import ProjectTemplateAPI from 'state/ProjectTemplate/ProjectTemplateAPI';

const adapter = createEntityAdapter<ProjectTemplate>();

interface ProjectTemplatesExtraState {
  status: Status;
  searchParam: string;
  pagination: { limit: number; offset: number };
  sorting: { orderBy: string[]; order: SortingType };
}

const initialState = adapter.getInitialState<ProjectTemplatesExtraState>({
  status: SLICE_STATUS.IDLE,
  searchParam: '',
  pagination: { limit: 15, offset: 0 },
  sorting: { order: 'asc', orderBy: [] },
});

function mapLegacyTemplate(t: LegacyProjectTemplate): ProjectTemplate {
  return {
    id: t.id!,
    name: t.name,
    description: t.description ?? null,
    ownerId: t.owner_id ?? null,
    access: t.access,
    status: t.status,
    resourceType: t.resource_type,
    system: t.system ?? false,
    exclusive: t.exclusive ?? false,
    hidden: t.hidden,
    creatorId: t.creator_id ?? null,
    createdAt: t.createdAt ? new Date(t.createdAt).toISOString() : '',
    updatedAt: t.updatedAt ? new Date(t.updatedAt).toISOString() : '',
    owner: t.owner
      ? {
          id: t.owner.id ?? '',
          name: `${get(t, 'owner.data.firstName', '')} ${get(
            t,
            'owner.data.lastName',
            ''
          )}`.trim(),
          firstName: get(t, 'owner.data.firstName'),
          lastName: get(t, 'owner.data.lastName'),
          email: get(t, 'owner.data.email', ''),
          status: t.owner.status ?? null,
          avatarUrl: t.owner.avatar_url ?? null,
          userType: t.owner.type ?? '',
        }
      : null,
    assignedTeams: [],
  };
}

function concurrencyCondition(
  _: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  return getState().projectTemplates.status !== SLICE_STATUS.LOADING;
}

interface FetchParams {
  useNewApi: boolean;
}

export const fetchProjectTemplates = createAsyncThunk<
  ProjectTemplate[],
  FetchParams,
  ThunkApi
>(
  'projectTemplates/FETCH_TEMPLATES',
  async ({ useNewApi }, { extra }) => {
    if (useNewApi) {
      return extra.getProjectTemplatesApi().getAll();
    }
    const legacy = await projectTemplatesAPI.fetchProjectTemplates({});
    return (legacy as LegacyProjectTemplate[]).map(mapLegacyTemplate);
  },
  { condition: concurrencyCondition }
);

export const fetchProjectTemplatesForSelection = createAsyncThunk<
  ProjectTemplate[],
  FetchParams,
  ThunkApi
>(
  'projectTemplates/FETCH_TEMPLATES_FOR_SELECTION',
  async ({ useNewApi }, { extra }) => {
    if (useNewApi) {
      return extra.getProjectTemplatesApi().getForSelection();
    }
    const legacy = await projectTemplatesAPI.fetchProjectTemplates({
      status: 'published',
    });
    return (legacy as LegacyProjectTemplate[]).map(mapLegacyTemplate);
  },
  { condition: concurrencyCondition }
);

export const addProjectTemplate = createAsyncThunk(
  'projectTemplates/ADD_TEMPLATE',
  async (params: { template: LegacyProjectTemplate }) => {
    const {
      project_template_fields,
      creator_id,
      owner,
      creator,
      ...otherData
    } = params.template;
    const newTemplate = await projectTemplatesAPI.addNewProjectTemplate({
      ...otherData,
    });
    if (newTemplate.id && project_template_fields.length > 0) {
      const newFields = project_template_fields.map(
        ({ field_template, ...templateField }) => ({
          ...templateField,
          project_template_id: newTemplate.id,
          currentTemplateVersion: newTemplate.version,
        })
      );
      newTemplate.project_template_fields =
        await ProjectTemplateAPI.bulkCreateProjectTemplateFields(newFields);
    }
    return mapLegacyTemplate(newTemplate);
  }
);

export const deleteProjectTemplate = createAsyncThunk(
  'projectTemplates/DELETE_TEMPLATE',
  async (params: { templateId: string }) => {
    const response = await projectTemplatesAPI.deleteProjectTemplate(
      params.templateId
    );
    return response.deletedRowId as string;
  }
);

const projectTemplatesSlice = createSlice({
  name: 'projectTemplates',
  initialState,
  reducers: {
    setSearchParam(state, action: PayloadAction<string>) {
      state.searchParam = action.payload.toLocaleLowerCase().trim();
    },
    updateTemplatePagination(
      state,
      action: PayloadAction<{ limit: number; offset: number }>
    ) {
      state.pagination = action.payload;
    },
    setTemplatesOrder(
      state,
      action: PayloadAction<{ order: SortingType; orderBy: string[] }>
    ) {
      state.sorting = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectTemplates.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjectTemplates.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload);
        state.status = SLICE_STATUS.IDLE;
      })
      .addCase(fetchProjectTemplates.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(fetchProjectTemplatesForSelection.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjectTemplatesForSelection.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload);
        state.status = SLICE_STATUS.IDLE;
      })
      .addCase(fetchProjectTemplatesForSelection.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(addProjectTemplate.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload);
      })
      .addCase(deleteProjectTemplate.fulfilled, (state, action) => {
        adapter.removeOne(state, action.payload);
      });
  },
});

export const { setSearchParam, updateTemplatePagination, setTemplatesOrder } =
  projectTemplatesSlice.actions;

const adapterSelectors = adapter.getSelectors<RootState>(
  (state) => state.projectTemplates
);

export const selectAllProjectTemplates = adapterSelectors.selectAll;

export const selectProjectTemplatesStatus = (state: RootState): Status =>
  state.projectTemplates.status;

const selectSearchParam = (state: RootState) =>
  state.projectTemplates.searchParam;
const selectPagination = (state: RootState) =>
  state.projectTemplates.pagination;
const selectSorting = (state: RootState) => state.projectTemplates.sorting;

export const projectTemplatesForTable = createSelector(
  [
    selectAllProjectTemplates,
    selectSearchParam,
    selectPagination,
    selectSorting,
  ],
  (templates, searchParam, pagination, sorting) => {
    let filtered = templates.filter((template) => {
      if (!searchParam) return true;
      return (
        template.name
          ?.toLocaleLowerCase()
          .includes(searchParam.toLocaleLowerCase()) || false
      );
    });

    filtered = orderBy(
      filtered,
      (template) =>
        sorting.orderBy.map((orderRoute) => {
          switch (orderRoute) {
            case TEMPLATE_SORTING.UPDATED_AT[0]:
              return get(template, orderRoute);
            case TEMPLATE_SORTING.OWNER[0]:
            case TEMPLATE_SORTING.OWNER[1]: {
              const label = `${
                get(template, TEMPLATE_SORTING.OWNER[0]) || ''
              } ${get(template, TEMPLATE_SORTING.OWNER[1])}`.trim();
              return label ? label.toLocaleLowerCase() : '';
            }
            case TEMPLATE_SORTING.STATUS[0]:
              return intl.get(
                `SETTINGS_PAGE.PROJECTS_PAGE.${get(template, orderRoute)}`
              );
            default: {
              const label = `${get(template, orderRoute) || ''}`.trim();
              return label ? label.toLocaleLowerCase() : '';
            }
          }
        }),
      [sorting.order, sorting.order]
    );

    return {
      total: templates.length,
      data: filtered.slice(pagination.offset, pagination.limit),
    };
  }
);

export const selectAllTemplates = selectAllProjectTemplates;

export default projectTemplatesSlice.reducer;
